// Control logic for interaction between TaskCard components. This is the shared
// logic between list-view and task-view.

import Vue from 'vue';
import { bulkNestAtEnd, bulkRescheduleAtEnd } from './ordered-relation.js';
import interceptBackButton from './intercept-back-button.js';
import { isSubtask } from './utils.js';

export default class TaskCardsController {
  constructor(defaultDB, reactiveCache, $router) {
    this.defaultDB = defaultDB;
    this.reactiveCache = reactiveCache;
    this.$router = $router;
    this.selectedTasksIds = null;
    this.attachCandidatesIds = null;
    this.editModeTaskId = null;
    this.focusOnNewTaskCard = false;
    this.clearBackButtonHandler = null;
    this.pendingHistoryAction = Promise.resolve();
    this.$router.beforeEach(async (_to, _from, next) => {
      // Avoid race conditions between $router.push() and pending history.back()
      // calls coming from leaveSpecialState.
      await this.pendingHistoryAction;
      next();
    });
  }
  selectedTasks() {
    const ids = Object.keys(this.selectedTasksIds);
    return ids.map((id) => this.reactiveCache.docs[id]);
  }
  get wrapperClasses() {
    // TODO
    return {
      'nesting-selection': this.attachCandidatesIds != null,
      'selection': this.selectedTasksIds != null,
    };
  }
  // For tracking user activity.
  incrementCounter(name) {
    const data = JSON.parse(localStorage.getItem(name + '-count'));
    const count = data === null ? 0 : data;
    localStorage.setItem(name + '-count', JSON.stringify(count + 1));
  }
  enterSelectionMode() {
    if (!this.selectedTasksIds) {
      this.leaveSpecialState();
      this.selectedTasksIds = {};
      this.incrementCounter('selection-mode')
    }
  }
  select(task) {
    this.enterSelectionMode();
    Vue.set(this.selectedTasksIds, task._id, true);
  }
  unselect(task) {
    if (!this.selectedTasksIds) {
      return;
    }
    Vue.delete(this.selectedTasksIds, task._id);
    if (Object.keys(this.selectedTasksIds).length === 0) {
      this.selectedTasksIds = null;
    }
  }
  // Note: attachCandidatesIds will be used directly, without copying.
  _startAttaching(attachCandidatesIds) {
    this.leaveSpecialState();
    this.attachCandidatesIds = attachCandidatesIds;
    this.pendingHistoryAction.then(() => {
      // Note: pushState works synchonously (immediately changes the history),
      // so there is no pending action added.
      this.clearBackButtonHandler =
        interceptBackButton(() => this.leaveSpecialState());
    });
  }
  startAttaching(taskOrTasks) {
    if ('_id' in taskOrTasks) {
      // Assume that having _id means it's a single task.
      this._startAttaching({ [taskOrTasks._id]: true });
    } else {
      const idsSet = {};
      for (const task of taskOrTasks) {
        // No need for Vue.set because it's not attached to the reactive object
        // yet.
        idsSet[task._id] = true;
      }
      this._startAttaching(idsSet);
    }
  }
  startAttachingSelection() {
    if (!this.selectedTasksIds ||
      Object.keys(this.selectedTasksIds).length === 0) {
      return;
    }
    this._startAttaching(Object.assign({}, this.selectedTasksIds));
  }
  startEditMode(task) {
    this.editModeTaskId = task._id;
    this.pendingHistoryAction.then(() => {
      // Note: pushState works synchonously (immediately changes the history),
      // so there is no pending action added.
      this.clearBackButtonHandler =
        interceptBackButton(() => this.leaveSpecialState());
    });
    this.incrementCounter('edit-task')
  }
  openInTaskView(task) {
    this.$router.push(`/task/${task._id}`)
    this.incrementCounter('open-task')
  }
  handleClickedCard(task, event) {
    if (event.ctrlKey) {
      this.enterSelectionMode()
    }
    if (this.selectedTasksIds) {
      if (task._id in this.selectedTasksIds) {
        this.unselect(task);
      } else {
        this.select(task);
      }
    } else if (!this.attachCandidatesIds) {
      if (task._id !== this.editModeTaskId) {
        this.leaveSpecialState();
        if (task.getChildren().length || task.type === "event") {
          this.openInTaskView(task);
        } else {
          this.startEditMode(task);
        }
      } else {
        /* Do nothing. */
      }
      return;
    } else {
      // Handle click in nesting state.
      this.finalizeAttaching(task._id);
    }
  }
  // Returns objects from reactiveCache, not copies.
  getTasksInDisplayOrder(idsArray) {
    const tasks = idsArray.map((id) => this.reactiveCache.docs[id]);
    const kParentClassRank = {
      'task': 0,
      'day': 1,
      'week': 2,
      'month': 3,
      'backlog': 4,
    };
    tasks.sort((t1, t2) => {
      for (const relation of ['plannedFor', 'subtaskOf']) {
        if (Boolean(t1[relation]) != Boolean(t2[relation])) {
          return t1[relation] ? -1 : 1;
        }
        if (!t1[relation]) {
          continue;
        }
        // Sort by this relation.
        const rank1 = kParentClassRank[t1[relation].parentClass];
        const rank2 = kParentClassRank[t2[relation].parentClass];
        if (rank1 !== rank2) {
          return rank1 < rank2 ? -1 : 1;
        }
        if (t1[relation].parentKey !== t2[relation].parentKey) {
          return (
            t1[relation].parentKey < t2[relation].parentKey ? -1 : 1);
        }
        if (t1[relation].seq === t2[relation].seq) {
          // Equal seqs, fall back to _id. This is how ordered-relation handles
          // seq clashes.
          return t1._id < t2._id ? -1 : 1;
        }
        return t1[relation].seq < t2[relation].seq ? -1 : 1;
      }
      // No relation suitable for sorting, fall back to _id.
      return t1._id < t2._id ? -1 : 1;
    });
    return tasks;
  }
  getOrderedAttachCandidates() {
    return this.getTasksInDisplayOrder(Object.keys(this.attachCandidatesIds));
  }
  getOrderedSelectedTasks() {
    return this.getTasksInDisplayOrder(Object.keys(this.selectedTasksIds));
  }
  async finalizeAttaching(newParentTaskId) {
    if (newParentTaskId in this.attachCandidatesIds) {
      // Click on the attachCandidate, cancel the nesting.
      this.leaveSpecialState();
      return;
    }

    const tasks =
      this.getOrderedAttachCandidates()
        .map((t) => this.reactiveCache.docs[t._id]);

    // These will no longer be planned.
    for (const task of tasks) {
      if (!isSubtask(task)) {
        delete task.plannedFor;
      }
    }
    this.reactiveCache.freezeUntil(
      bulkNestAtEnd({
        reactiveCache: this.reactiveCache,
        tasks,
        newPPtr: {
          parentClass: 'task',
          parentKey: newParentTaskId,
        },
      })
        .catch((error) => {
          // TODO: Show the error in UI, handle it sensibly.
          console.error(error);
        }));
    this.leaveSpecialState();
  }
  async dropAtSectionEnd(section) {
    const tasks =
      this.getOrderedAttachCandidates()
        .map((t) => this.reactiveCache.docs[t._id]);

    // These will no longer be nested.
    for (const task of tasks) {
      if (!task.plannedFor) {
        delete task.subtaskOf;
      }
    }
    const options = {
      reactiveCache: this.reactiveCache,
      tasks,
      newPPtr: {
        parentClass: section.class,
        parentKey: section.key,
      },
    }
    if (section.relation === 'plannedFor') {
      this.reactiveCache.freezeUntil(
        bulkRescheduleAtEnd(options)
          .catch((error) => {
            // TODO: Show the error in UI, handle it sensibly.
            console.error(error);
          }));
    } else {
      this.reactiveCache.freezeUntil(
        bulkNestAtEnd(options)
          .catch((error) => {
            // TODO: Show the error in UI, handle it sensibly.
            console.error(error);
          }));
    }
    this.leaveSpecialState();
  }
  async leaveSpecialState() {
    // Cancel nesting and escape edit mode like on background click.
    this.selectedTasksIds = null;
    this.attachCandidatesIds = null;
    this.editModeTaskId = null;
    if (this.clearBackButtonHandler) {
      const savedClearBackButtonHandler = this.clearBackButtonHandler;
      this.clearBackButtonHandler = null;
      this.pendingHistoryAction =
        this.pendingHistoryAction.then(savedClearBackButtonHandler);
    }
    // Always return the pending action Promise. Even when there was no
    // clearBackButtonHandler set, it may be because there were several calls
    // one after another (the first one triggered clearBackButtonHandler,
    // but the history.back() call is still pending).
    return this.pendingHistoryAction;
  }
}
